import React, { useState } from "react";
import { Typography, Box, Button, IconButton, Tooltip, TextField, CircularProgress } from "@mui/material";
import { Copy, Gift, Building2, User, ExternalLink, MapPin, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useParams } from "react-router-dom";
import ShopeeIcon from "../../Assets/Icon/ShopeeIcon";
import { postGiftPurchaseApi } from "../../api/cardApi";
import DuitNowQR from "../../Assets/duitnow_QR.png";
import ThemeButton from "./ThemeButton";
import { getTranslations } from "../../utils/CardTranslations";

const GiftSection = ({ data, onClose, gifts = [], customMaxHeight, isPreviewMode = false, onGiftReserved }) => {
    const { uniqueCode } = useParams();
    const [copied, setCopied] = useState(false);
    const [reservingGift, setReservingGift] = useState(null);
    const [guestName, setGuestName] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dummyGifts = [
        { id: "dummy1", giftName: "Mini Vacuum Philips", giftLink: "https://shopee.com.my", isReserved: false, giftImage: "https://down-my.img.susercontent.com/file/sg-11134253-7rdyq-mdm484yb2vv81a.webp" },
        { id: "dummy2", giftName: "Microwave Oven Samsung", giftLink: "https://shopee.com.my", isReserved: true, giftImage: "https://down-my.img.susercontent.com/file/9a8b5da4100111ace60ecebd3f0adf55@resize_w450_nl.webp" },
        { id: "dummy3", giftName: "Set Pinggan Mangkuk Corelle", giftLink: "https://shopee.com.my", isReserved: false, giftImage: "https://down-my.img.susercontent.com/file/0aa270e82d443488750952ec5665010d.webp" }
    ];

    const displayGifts = isPreviewMode
        ? gifts
        : (gifts.length > 0 ? gifts : dummyGifts);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleReserve = async () => {
        if (!guestName || !guestPhone) {
            alert(t.giftReserveValidation);
            return;
        }

        setIsSubmitting(true);

        if (isPreviewMode) {
            // Mocking the API call for the preview so we don't save to DB
            setTimeout(() => {
                setIsSubmitting(false);
                setReservingGift(null);
                setGuestName("");
                setGuestPhone("");
                alert(t.giftReserveSuccessPreview);
            }, 1000);
            return;
        }

        if (data.status === 'PENDING PAYMENT' || data.status === 'DRAFT') {
            alert("Please complete the payment to enable this feature.");
            setIsSubmitting(false);
            return;
        }

        try {
            await postGiftPurchaseApi(uniqueCode, {
                guestName,
                phoneNumber: guestPhone,
                giftLink: reservingGift.giftLink,
                giftName: reservingGift.giftName,
                giftImage: reservingGift.giftImage,
                isReserved: true,
            });
            setReservingGift(null);
            setGuestName("");
            setGuestPhone("");
            if (onGiftReserved) await onGiftReserved();
            alert(t.giftReserveSuccess);
        } catch (error) {
            console.error("Error submitting gift purchase:", error);
            alert(t.giftReserveError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const themeColor = data?.buttonColour || data?.themeColour || "#1b9a9b";
    const t = getTranslations(data?.language);

    if (reservingGift) {
        return (
            <Box sx={{ fontFamily: data.fontStyle || "Poppins", textAlign: "center", width: "320px", margin: "auto" }}>
                <Box sx={{ px: 3, py: 4, border: "1px solid #ddd", borderRadius: "20px", background: "rgba(255, 255, 255, 0.95)", position: "relative" }}>
                    <IconButton
                        onClick={() => setReservingGift(null)}
                        sx={{ position: 'absolute', top: 10, left: 10, color: themeColor }}
                    >
                        <ArrowLeft size={20} />
                    </IconButton>

                    <Box sx={{ textAlign: 'left', mb: 2 }}>

                        <Typography variant="caption" sx={{ color: themeColor, fontWeight: 600, mb: 2, mt: 3, display: 'block' }}>{t.giftPurchaseInfo}</Typography>

                        <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: '12px', border: '1px solid #eee', mb: 2 }}>
                            <Box sx={{ display: 'column', gap: 1, alignItems: 'flex-start' }}>
                                <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Gift size={16} color={themeColor} />{reservingGift.giftName}</Typography>


                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <MapPin size={16} color={themeColor} />
                                    <Typography variant="body2" sx={{ color: '#666', display: 'block' }}>{t.giftDeliveryAddress}</Typography>
                                </Box>
                                <Box>

                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                        {data.brideAddress || t.giftAddressFallback}
                                    </Typography>
                                </Box>
                            </Box>

                            <ThemeButton
                                fullWidth
                                startIcon={<ShopeeIcon size={16} />}
                                onClick={() => window.open(reservingGift.link, "_blank")}
                                sx={{
                                    border: `1px solid ${themeColor}`,
                                    backgroundColor: 'none',
                                    width: "140px",
                                    color: themeColor,
                                    mt: 1.5,
                                    textTransform: 'none',
                                    borderRadius: '50px',
                                }}
                            >
                                {t.giftItemLink}
                            </ThemeButton>
                        </Box>

                        <Typography variant="caption" sx={{ color: themeColor, fontWeight: 600, mb: 2, display: 'block' }}>{t.giftYourInfo}</Typography>
                        <TextField
                            fullWidth
                            label={t.giftNameLabel}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "50px" } }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t.giftPhoneLabel}
                            InputLabelProps={{ shrink: true }}
                            value={guestPhone}
                            onChange={(e) => setGuestPhone(e.target.value)}
                            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "50px" } }}
                        />
                    </Box >

                    <ThemeButton
                        variant="contained"
                        fullWidth
                        onClick={handleReserve}
                        sx={{
                            bgcolor: themeColor,
                            color: 'white',
                            textTransform: 'none',

                        }}
                    >
                        {t.giftConfirmReserve}
                    </ThemeButton>
                </Box >
            </Box >
        );
    }

    return (
        <Box
            sx={{
                fontFamily: data.fontStyle || "Poppins, sans-serif",
                textAlign: "center",
                width: "320px",
                margin: "auto",
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 4,
                    pt: 4,
                    border: "1px solid #ddd",
                    borderRadius: "20px",
                    backdropFilter: "blur(3px)",
                    background: "rgba(255, 255, 255, 0.85)",
                    position: "relative",
                    maxHeight: customMaxHeight || '65vh',
                    overflowY: 'auto'
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {data.showQRBank && (
                        <>
                            <Box
                                sx={{
                                    width: "180px",
                                    height: "180px",
                                    margin: "0 auto 26px",
                                    p: 1.5,
                                    background: "#fff",
                                    borderRadius: "20px",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                                    border: `2px solid ${themeColor}10`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden"
                                }}
                            >
                                {data.bankQRImage ? (
                                    <img
                                        src={data.bankQRImage}
                                        alt="Bank QR"
                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                    />
                                ) : (
                                    <Box sx={{ color: '#ccc', textAlign: 'center' }}>
                                        <Typography variant="caption">{t.giftNoQR}</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    background: '#fcfcfc',
                                    borderRadius: '20px',
                                    p: 2,
                                    mb: 2,
                                    border: '1px solid #f0f0f0',
                                    textAlign: 'left'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
                                    <Building2 size={16} color={themeColor} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1 }}>
                                            Bank
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#343434ff' }}>
                                            {data.bankName || t.giftUpdatePlaceholder}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
                                    <User size={16} color={themeColor} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1 }}>
                                            {t.giftAccountName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#343434ff' }}>
                                            {data.bankAccountName || t.giftUpdatePlaceholder}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                    pt: 1.5,
                                    borderTop: '1px dashed #eee'
                                }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1 }}>
                                            {t.giftAccountNumber}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: themeColor, fontSize: '1.1rem' }}>
                                            {data.bankAccountNumber || "000000000000"}
                                        </Typography>
                                    </Box>
                                    <Tooltip title={copied ? t.giftCopied : t.giftCopy}>
                                        <IconButton
                                            onClick={() => handleCopy(data.bankAccountNumber)}
                                            sx={{
                                                background: copied ? '#4caf5015' : `${themeColor}10`,
                                                color: copied ? '#4caf50' : themeColor,
                                                '&:hover': { background: copied ? '#4caf5020' : `${themeColor}20` }
                                            }}
                                        >
                                            <Copy size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </>
                    )}

                    {displayGifts.length > 0 && data.showWishlistGift && (
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Gift size={18} color={themeColor} /> {t.giftWishlistTitle}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {displayGifts.map((gift) => (
                                    <Box
                                        key={gift.id || gift.giftName}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#fcfcfc',
                                            border: '1px solid #f0f0f0',
                                            borderRadius: "20px",
                                            gap: 2,
                                            p: 1.5,
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '12px',
                                            bgcolor: `${themeColor}10`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            flexShrink: 0
                                        }}>
                                            {gift.giftImage ? (
                                                <img src={gift.giftImage} alt={gift.giftName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Gift size={20} color={themeColor} />
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {gift.giftName}
                                            </Typography>

                                            {gift.isReserved ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                    <CheckCircle2 size={14} color="#4caf50" />
                                                    <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                                        {t.giftReserved}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setReservingGift(gift)}
                                                    sx={{
                                                        mt: 0.5,
                                                        textTransform: 'none',
                                                        borderRadius: '20px',
                                                        fontSize: '0.7rem',
                                                        py: 0,
                                                        borderColor: themeColor,
                                                        color: themeColor,
                                                        '&:hover': { borderColor: themeColor, background: `${themeColor}05` }
                                                    }}
                                                >
                                                    {t.giftReserveBtn}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {isPreviewMode && !data.showQRBank && !data.showWishlistGift && (
                        <Box sx={{ py: 6, textAlign: 'center', opacity: 0.6 }}>
                            <Gift size={40} color={themeColor} style={{ opacity: 0.5, marginBottom: '12px' }} />
                            <Typography variant="body2" sx={{ color: '#333', mb: 1, fontWeight: 600 }}>
                                {t.giftEmptyTitle} <br /> {t.giftEmptyAnd}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#555' }}>
                                {t.giftEmptyDesc}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default GiftSection;
